/* eslint-disable max-lines */
import {Lang} from 'yougile-lang'
import {logger, ObjUtil, Time, Util, UUID} from 'yougile-util'
import {
  CustomStickersTypes,
  DataObjectId,
  DeadlineStickerValue,
  findCurrentIntervalState,
  Hub,
  HubLike,
  isAuthor,
  SandboxTree,
  SprintStickerState,
  Tag,
  TimerStickerValue,
  youGileUserId
} from 'yougile-model'
import {Stickers} from '@api/model/stickers'
import {PermissionsChecker} from '@client/permissions-checker'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {Sync} from '@client/sync'
import {Backend} from '@common/backend'
import {MessageBox} from '@components/message-box'
import {noDefaultState} from '@components/sticker/my-stickers/board-stickers/sticker-settings/use-common-handlers'
import {globals} from '../globals-base'
import {Sticker, stickerCtl, systemStickerCtl} from './sticker'
import {StickerFilter} from './sticker-filter'

const boardFiltersStorageKey = 'board-filters-v2'

interface BoardFilterStorage {
  [boardId: string]: {
    [stickerId: string]:
      | {
          include: string[] // sticker states to include to filter
        }
      | undefined
  }
}

/**
 * Manages board filtering.
 * Boards can be filtered with some condition on sticker values.
 */
export class BoardFilters {
  private static listeners: Map<UUID, () => void> = new Map()
  public static current: BoardFilterStorage = {}

  public static stickerFilterFromBoard(board: Hub): StickerFilter {
    return {
      getChosen(stickerId: DataObjectId): string[] {
        const box = Sync.get(board)
        const sticker = box.get(stickerId) as Tag
        return BoardFilters.getForSticker(board, sticker)
      },
      onToggle(stickerId: DataObjectId, state: string): void {
        const box = Sync.get(board)
        const sticker = box.get(stickerId) as Tag
        BoardFilters.toggleFilter(board, sticker, state)
      },
      getTimetrackingBoard(): Hub {
        return board
      },
      setFilter(stickerId: DataObjectId, values: string[]): void {
        const box = Sync.get(board)
        const sticker = box.get(stickerId) as Tag
        BoardFilters.setFilter(board, sticker, values)
      }
    }
  }

  public static load(): void {
    const raw = Backend.loadLocal(boardFiltersStorageKey)
    if (raw) {
      try {
        BoardFilters.current = JSON.parse(raw)
      } catch (e) {
        logger.error('Incorrect board filters format', raw)
      }
    }
    BoardFilters.trigger()
  }

  public static save(): void {
    const value = JSON.stringify(BoardFilters.current)
    Backend.saveLocal(boardFiltersStorageKey, value)
    BoardFilters.trigger()
  }

  private static trigger(): void {
    Stickers.filterChange()
    ;[...BoardFilters.listeners.values()].forEach(cb => cb())
  }

  public static addListener(cb: () => void): UUID {
    const id = Util.UUID()
    BoardFilters.listeners.set(id, cb)
    return id
  }

  public static removeListener(id: UUID): void {
    BoardFilters.listeners.delete(id)
  }

  public static filter(board: Hub, tasks: Hub[]): Hub[] {
    const cur = BoardFilters.current[board.id]
    if (cur) {
      const box = Sync.get(board)
      Object.keys(cur).forEach((stickerId: DataObjectId) => {
        const sticker = box.get(stickerId) as Tag
        const includeArr = BoardFilters.getForSticker(board, sticker)
        const include: Set<string> = new Set(includeArr)
        if (
          !sticker ||
          sticker.deleted ||
          !Sticker.isInBoard(board, sticker) ||
          include.size === 0
        ) {
          delete cur[stickerId]
          BoardFilters.save()
          return
        }
        if (sticker.title === 'Deadline') {
          const t1 = +includeArr[0]
          const t2 = +includeArr[1]
          tasks = tasks.filter(h => {
            const st = h.stickers[stickerId] as DeadlineStickerValue
            return (
              st &&
              st.current &&
              st.current.deadline &&
              Time.dayBetween(st.current.deadline, t1, t2)
            )
          })
        } else {
          tasks = tasks.filter(h => {
            const v = h.stickers[stickerId]
            if (!v) {
              return false
            } else if (v instanceof Array) {
              return !!v.find(val => include.has(val))
            } else {
              return include.has(h.stickers[stickerId])
            }
          })
        }
      })
    }
    return tasks
  }

  public static pinFiltersToTask(
    board: Hub,
    task: Hub,
    dragIntoSameCol = false,
    type: 'create-task' | 'create-via-calendar' | 'task-drop'
  ): void {
    const box = Sync.get(board)
    let needCommit = false
    const isCreateTask =
      type === 'create-task' || type === 'create-via-calendar'

    const stickersSettings = board.data.stickersSettings ?? {}
    const stickersSettingsKeys = ObjUtil.keys(stickersSettings)

    for (const stickerId of stickersSettingsKeys) {
      if (!board.stickers[stickerId] || task.stickers[stickerId]) {
        continue
      }

      const sticker = Sticker.getById(box, stickerId)
      const stickerType = sticker?.properties?.stickerType
      const settings = stickersSettings?.[stickerId]

      if (isCreateTask && stickerType === 'string') {
        if (settings.hasDefaultState && settings.defaultState) {
          task.stickers[stickerId] = settings.defaultState
          needCommit = true
        }
      }

      if (isCreateTask && sticker && stickerType === 'sprint') {
        if (settings.hasDefaultState) {
          if (settings.defaultState === noDefaultState) {
            const values = Object.values(
              sticker.states.index
            ) as SprintStickerState[]

            const state = findCurrentIntervalState(values)
            if (state) {
              task.stickers[stickerId] = state.id
              needCommit = true
            }
          } else {
            task.stickers[stickerId] = settings.defaultState
            needCommit = true
          }
        }
      }

      if (isCreateTask && stickerType === 'user' && settings.hasDefaultState) {
        if (isAuthor(settings)) {
          task.stickers[stickerId] = [task.data?.by]
          needCommit = true
        } else {
          // TODO fix type HubJSON
          task.stickers[stickerId] = settings.defaultState as any
          needCommit = true
        }
      }

      if (
        stickerType === 'stopwatch' &&
        settings.hasDefaultState &&
        !dragIntoSameCol
      ) {
        const newVal: TimerStickerValue = {
          current: {
            time: '0s',
            by: task.data?.by ?? youGileUserId,
            timestamp: Time.now(),
            running: true
          },
          history: []
        }
        task.stickers[stickerId] = newVal
        needCommit = true
      }
    }

    // TODO! this action should be done automatically on backend
    // https://test-ru.yougile.com/team/e773f3991465/#chat:6c7085a6d965
    if (needCommit) {
      task.commit()
    }

    if (isCreateTask) {
      this.pinFilterDeadlineToTask(board, box, task)
    }
  }

  private static pinFilterDeadlineToTask(
    board: Hub,
    box: SandboxTree,
    task: Hub
  ): void {
    const filters = BoardFilters.current[board.id]
    if (!filters) {
      return
    }

    const deadline = systemStickerCtl.Deadline.getSticker(box)
    const deadlineId = deadline ? deadline.id : undefined

    Object.keys(filters).forEach(stickerId => {
      const cur = BoardFilters.current[board.id][stickerId]
      if (cur && cur.include) {
        const sticker = box.get(stickerId as DataObjectId) as Tag
        if (BoardFilters.filter(board, [task]).length > 0) {
          return
        }
        let value: any
        if (stickerId === deadlineId) {
          if (cur.include && cur.include.length === 2 && +cur.include[1]) {
            const v: DeadlineStickerValue = {
              current: {
                deadline: +cur.include[1],
                timestamp: Time.now(),
                notifyBefore: 0,
                withTime: false,
                by: globals.currentUserId
              },
              history: []
            }
            value = v
          } else {
            return
          }
        } else {
          value = cur.include[0]
        }
        if (PermissionsChecker.checkEditTaskStickers(task, sticker, value)) {
          if (!task.stickers[sticker.id]) {
            Sticker.pin(sticker, task.location, task, value)
          }
        } else {
          MessageBox.warn(Lang.msg.taskIsOutOfFilters)
        }
      }
    })
  }

  public static toggleFilter(
    board: HubLike,
    sticker: Tag,
    value: string
  ): void {
    const cur = BoardFilters.getForSticker(board, sticker)
    const ix = cur.indexOf(value)
    if (ix > -1) {
      cur.splice(ix, 1)
    } else {
      cur.push(value)
    }
    BoardFilters.setFilter(board, sticker, cur)
  }

  public static setFilter(
    board: HubLike,
    sticker: Tag,
    values: string[]
  ): void {
    BoardFilters.current[board.id] ||= {}
    BoardFilters.current[board.id][sticker.id] =
      values.length > 0 ? {include: values} : undefined
    BoardFilters.save()
  }

  public static getForSticker(board: HubLike, sticker: Tag): string[] {
    if (!sticker) {
      return []
    }
    const boardFilter = BoardFilters.current[board.id]
    const filterData = boardFilter?.[sticker.id]
    if (!filterData) {
      return []
    }
    const includeArray = filterData.include ?? []
    let res = JSON.parse(JSON.stringify(includeArray)) as string[]

    if (CustomStickersTypes.includes(Sticker.getType(sticker))) {
      res = res.filter(st => st in sticker.states.index)
    }
    if (Sticker.getType(sticker) === 'user') {
      const box = Sync.getCurrentSandbox()
      res = res.filter(st => box.isUserInCompany(st as DataObjectId))
    }
    return res
  }

  public static autoFilterSprints(board: Hub): void {
    if (SharedBoardsClient.getId()) {
      return
    }
    if (!globals.boardsAutoFiltered.has(board.id)) {
      globals.boardsAutoFiltered.add(board.id)
      const sprint = Sticker.list(board).find(
        s =>
          Sticker.getType(s) === 'sprint' &&
          (board.data.stickersSettings?.[s.id]?.filterByCurrentValue ||
            board.data.stickersSettings?.[s.id]?.filterByCurrentValue ===
              undefined)
      )
      if (
        BoardFilters.current[board.id] &&
        sprint &&
        BoardFilters.current[board.id][sprint.id]
      ) {
        return
      }
      if (sprint) {
        const states = stickerCtl.Sprint.listStates(sprint)
        const state = findCurrentIntervalState(states)
        if (state) {
          BoardFilters.toggleFilter(board, sprint, state.id)
        }
      }
    }
  }
}
